import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { UploadSimple, X, FileImage, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Upload() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [companies, setCompanies] = useState([]);
    const [companyId, setCompanyId] = useState(searchParams.get("company") || "");
    const [reportType, setReportType] = useState("daily_ops");
    const [reportDate, setReportDate] = useState(new Date().toISOString().slice(0, 10));
    const [notes, setNotes] = useState("");
    const [files, setFiles] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [dragOver, setDragOver] = useState(false);
    const [busy, setBusy] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        api.get("/companies").then((r) => {
            setCompanies(r.data);
            if (!companyId && r.data[0]) setCompanyId(r.data[0].id);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSelect = (filesList) => {
        const arr = Array.from(filesList).filter((f) => {
            const name = (f.name || "").toLowerCase();
            return f.type.startsWith("image/") || /\.(csv|xlsx|xlsm|tsv)$/i.test(name);
        });
        setFiles((prev) => [...prev, ...arr]);
        arr.forEach((f) => {
            if (f.type.startsWith("image/")) {
                const reader = new FileReader();
                reader.onload = (e) => setPreviews((p) => [...p, { name: f.name, url: e.target.result, kind: "image" }]);
                reader.readAsDataURL(f);
            } else {
                setPreviews((p) => [...p, { name: f.name, url: "", kind: "sheet" }]);
            }
        });
    };

    const removeAt = (idx) => {
        setFiles((p) => p.filter((_, i) => i !== idx));
        setPreviews((p) => p.filter((_, i) => i !== idx));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!companyId) { toast.error("Select a company"); return; }
        if (files.length === 0) { toast.error("Add at least one screenshot, photo or spreadsheet"); return; }
        setBusy(true);
        try {
            const form = new FormData();
            form.append("company_id", companyId);
            form.append("report_type", reportType);
            form.append("report_date", reportDate);
            form.append("notes", notes);
            files.forEach((f) => form.append("files", f));
            const { data } = await api.post("/reports/upload", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Report extracted by Gemini 3 Pro");
            navigate(`/reports/${data.id}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="p-8 lg:p-10 max-w-[1200px]" data-testid="upload-page">
            <div className="mb-8">
                <div className="overline mb-2">Ingest</div>
                <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">UPLOAD DAILY REPORT</h1>
                <p className="text-sm text-muted2 mt-2 max-w-xl">
                    Drop in WhatsApp screenshots, Google Sheets exports, handwritten field notes or invoices.
                    Gemini 3 Pro will extract every figure and turn it into a structured report.
                </p>
            </div>

            <form onSubmit={onSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 space-y-4">
                    <div
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setDragOver(false);
                            if (e.dataTransfer.files) onSelect(e.dataTransfer.files);
                        }}
                        onClick={() => inputRef.current?.click()}
                        data-testid="upload-dropzone"
                        className={`grid-card p-12 text-center cursor-pointer transition-all ${
                            dragOver ? "border-ink bg-paper" : "border-dashed border-2 border-rule hover:border-ink"
                        }`}
                    >
                        <UploadSimple size={36} className="mx-auto mb-3 text-ink" />
                        <div className="font-heading text-xl font-bold tracking-tight">Drop pictures or spreadsheets here</div>
                        <div className="text-xs text-muted2 mt-1">PNG, JPG, WEBP · CSV, XLSX · max 10MB each · multiple allowed</div>
                        <input
                            ref={inputRef}
                            type="file"
                            multiple
                            accept="image/*,.csv,.xlsx,.xlsm,.tsv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                            className="hidden"
                            data-testid="upload-input"
                            onChange={(e) => onSelect(e.target.files)}
                        />
                    </div>

                    {previews.length > 0 ? (
                        <div className="grid-card p-4">
                            <div className="overline mb-3">{previews.length} file{previews.length > 1 ? "s" : ""} queued</div>
                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                {previews.map((p, i) => (
                                    <div key={i} className="relative border border-rule rounded-sm overflow-hidden group" data-testid={`preview-${i}`}>
                                        {p.kind === "image" ? (
                                            <img src={p.url} alt={p.name} className="w-full h-28 object-cover" />
                                        ) : (
                                            <div className="w-full h-28 bg-paper flex flex-col items-center justify-center gap-1">
                                                <FileImage size={22} className="text-muted2" />
                                                <span className="text-[10px] uppercase tracking-wider text-muted2">spreadsheet</span>
                                            </div>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => removeAt(i)}
                                            data-testid={`remove-preview-${i}`}
                                            className="absolute top-1 right-1 bg-ink text-paper w-6 h-6 rounded-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <X size={12} />
                                        </button>
                                        <div className="absolute bottom-0 inset-x-0 bg-ink/80 text-paper text-[10px] px-1 py-0.5 truncate">{p.name}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>

                <div className="space-y-4">
                    <div className="grid-card p-6 space-y-4">
                        <div>
                            <label className="overline block mb-1.5">Company</label>
                            <select
                                data-testid="upload-company-select"
                                value={companyId}
                                onChange={(e) => setCompanyId(e.target.value)}
                                className="input-base"
                                required
                            >
                                <option value="">— Select —</option>
                                {companies.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="overline block mb-1.5">Report type</label>
                            <div className="grid grid-cols-2 gap-2">
                                {[
                                    { v: "daily_ops", label: "Daily Ops" },
                                    { v: "financial", label: "Financial" },
                                ].map((opt) => (
                                    <button
                                        type="button"
                                        key={opt.v}
                                        data-testid={`type-${opt.v}`}
                                        onClick={() => setReportType(opt.v)}
                                        className={`py-2 text-xs rounded-sm border transition-colors ${
                                            reportType === opt.v ? "bg-ink text-paper border-ink" : "bg-surface text-ink border-rule hover:border-ink"
                                        }`}
                                    >{opt.label}</button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="overline block mb-1.5">Report date</label>
                            <input
                                data-testid="upload-date"
                                type="date"
                                value={reportDate}
                                onChange={(e) => setReportDate(e.target.value)}
                                min="2020-01-01"
                                max={new Date().toISOString().slice(0, 10)}
                                className="input-base"
                            />
                            <div className="text-[11px] text-faint mt-1">Backdated entries supported from 2020 onwards.</div>
                        </div>
                        <div>
                            <label className="overline block mb-1.5">Notes (optional)</label>
                            <textarea
                                data-testid="upload-notes"
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder="Anything Gemini should know about these images?"
                                className="input-base resize-none"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="upload-submit"
                            className="w-full bg-ink text-paper py-3 rounded-sm text-sm font-medium hover:bg-klein transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                        >
                            {busy ? "Analysing with Gemini 3 Pro…" : (<><Lightning size={16} weight="fill" /> Extract & generate report</>)}
                        </button>
                    </div>
                    <div className="grid-card p-6 text-xs text-muted2 space-y-2">
                        <div className="flex gap-2 items-start">
                            <FileImage size={14} className="text-ink shrink-0 mt-0.5" />
                            <span>Supported: WhatsApp screenshots, photos of paper logs, Google Sheets / Excel exports (.xlsx, .csv).</span>
                        </div>
                        <div className="flex gap-2 items-start">
                            <Lightning size={14} className="text-ink shrink-0 mt-0.5" />
                            <span>Processing is server-side via Gemini 3 Pro · no images leave your group.</span>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
}
